import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { FolderOff as FolderOffIcon, Home as HomeIcon } from '@mui/icons-material';
import { BRAND_COLORS } from '../../styles/theme';
import { useTranslation } from '../../i18n';

/**
 * The dead-deep-link panel (STD-029 B-5).
 *
 * Two kinds, and only two, because there are only two things the server tells us
 * that a user can act on:
 *
 * - `not-found` — absent, tombstoned, or not visible to this caller. **One
 *   message for all three.** The endpoint answers 404 for each precisely so the
 *   client cannot become an existence oracle; splitting the copy here would
 *   rebuild that oracle in the browser.
 * - `not-a-folder` — the link names a FILE. The caller can demonstrably see it
 *   (that is what makes the 422 safe to send), so saying so costs nothing and
 *   saves the reader from thinking their link is broken.
 *
 * A listing OUTAGE is deliberately not here: it stays the inline `<Alert>` above
 * the browser, so a registry failure never reads as "your folder is gone".
 *
 * Presentational only — the classification is `folderLoadFailure`, which is pure
 * and unit-tested. There is nothing in this file a test tier could hold.
 */
export interface FolderNotFoundProps {
  kind: 'not-found' | 'not-a-folder';
  /** Back to `/files`, the caller's own root — the one url that always exists. */
  onBackToRoot: () => void;
}

const FolderNotFound: React.FC<FolderNotFoundProps> = ({ kind, onBackToRoot }) => {
  const { t } = useTranslation();
  const isNotAFolder = kind === 'not-a-folder';

  return (
    <Box
      data-testid="folder-not-found"
      data-kind={kind}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
        py: 8,
        textAlign: 'center',
      }}
    >
      <FolderOffIcon sx={{ fontSize: 48, color: BRAND_COLORS.forestGreen.main, opacity: 0.5 }} />
      <Typography variant="h6" sx={{ color: BRAND_COLORS.forestGreen.main }}>
        {t(isNotAFolder ? 'fileManagement.notAFolderTitle' : 'fileManagement.folderNotFoundTitle')}
      </Typography>
      <Typography variant="body2" sx={{ color: '#666', maxWidth: 420 }}>
        {t(isNotAFolder ? 'fileManagement.notAFolderBody' : 'fileManagement.folderNotFoundBody')}
      </Typography>
      <Button
        variant="contained"
        startIcon={<HomeIcon />}
        onClick={onBackToRoot}
        data-testid="go-to-files-root-btn"
        sx={{ mt: 1 }}
      >
        {t('fileManagement.backToMyFiles')}
      </Button>
    </Box>
  );
};

export default FolderNotFound;
